import { useState } from 'react';

export default function Pricing() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);

      const response = await fetch('https://readdy.ai/api/form/d4oppt7iu80n4l8qnjq0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pricing" className="relative py-0 overflow-hidden">
      <div className="relative h-[70vh] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/0ac72668a4582a64b1ac8f54b7872983.jpg"
            alt="City Network"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#0A0E27]/60"></div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-8 w-full">
          <div className="absolute left-20 top-[25%]">
            <h2 className="text-white leading-[1.2]">
              <div className="text-[72px] font-extralight lowercase">experience</div>
              <div className="text-[72px] font-bold uppercase">UNBREAKABLE</div>
              <div className="text-[72px] font-extralight lowercase">security</div>
            </h2>
          </div>

          <button
            onClick={() => scrollToSection('contact')}
            className="absolute left-20 bottom-[100px] flex items-center gap-0 bg-white rounded-full hover:pr-2 transition-all duration-300 group cursor-pointer whitespace-nowrap"
          >
            <span className="pl-8 pr-6 py-4 text-[#0D1B2A] font-semibold text-base">
              Start Free Trial
            </span>
            <div className="w-16 h-16 bg-[#0D1B2A] rounded-full flex items-center justify-center">
              <i className="ri-arrow-right-line text-2xl text-white"></i>
            </div>
          </button>

          <div className="absolute right-24 bottom-[30%] max-w-[400px]">
            <p className="text-white text-xl font-light leading-[1.6] text-shadow-lg">
              Protect your online privacy with<br />
              military-grade encryption technology
            </p>
          </div>
        </div>
      </div>

      <div className="bg-[#F8FAFB] py-24">
        <div className="max-w-[600px] mx-auto px-8">
          <div className="text-center mb-12">
            <h3 className="text-[48px] font-bold text-[#0D1B2A] mb-4">
              Get Started Today
            </h3>
            <p className="text-[#5F6C7B] text-lg">
              Sign up now and experience secure browsing with our VPN service
            </p>
          </div>

          <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-[#0D1B2A] mb-2">
                Full Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-5 py-4 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00D9FF] focus:border-transparent"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#0D1B2A] mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-5 py-4 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00D9FF] focus:border-transparent"
                placeholder="Enter your email address"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-[#0D1B2A] mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="w-full px-5 py-4 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00D9FF] focus:border-transparent"
                placeholder="Enter your phone number"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-[#00D9FF] text-white font-semibold rounded-lg hover:bg-[#00C4E6] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Submitting...' : 'Sign Up Now'}
            </button>

            {submitStatus === 'success' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm text-center">
                Thank you for signing up! We'll contact you soon.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm text-center">
                Something went wrong. Please try again.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
